import React, { useEffect, useRef, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    FormControlLabel,
    Grid,
    IconButton,
    Paper,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";

export const UNGROUPED_GROUP_LABEL = "Sin agrupar";

export const TextAddButton = ({ children, onClick, startIcon = <AddCircleOutlineOutlinedIcon /> }) => (
    <Button
        size="small"
        variant="text"
        startIcon={startIcon}
        onClick={onClick}
        sx={{ alignSelf: "flex-start", px: 0.5 }}>
        {children}
    </Button>
);

// Cabecera común de todo editor de automatización: volver, nombre editable en línea, descripción, grupo (carpeta de la
// lista, compartida por todos los tipos) e interruptor Activa/Pausada. `controls` agrega lo propio del tipo al lado del
// interruptor (por ejemplo, el enfriamiento de una regla).
const AutomationMetaHeader = ({
    automation,
    onChange,
    onBack,
    groupOptions = [],
    controls = null,
    namePlaceholder = "Nombre de la automatización",
}) => {
    const titleInputRef = useRef(null);
    const [editingTitle, setEditingTitle] = useState(true);
    const [showDescription, setShowDescription] = useState(!!automation?.description);
    const [showGroup, setShowGroup] = useState(!!automation?.notification_group);
    const title = automation?.name?.trim();

    useEffect(() => {
        if (!editingTitle || !titleInputRef.current) return undefined;

        const focusTimer = window.setTimeout(() => {
            const input = titleInputRef.current;
            if (!input) return;

            input.focus();
            const cursorPosition = input.value?.length || 0;
            input.setSelectionRange?.(cursorPosition, cursorPosition);
        }, 0);

        return () => window.clearTimeout(focusTimer);
    }, [editingTitle, automation?.id]);

    const renderTitle = () => {
        if (editingTitle || !title) {
            return (
                <TextField
                    autoFocus
                    inputRef={titleInputRef}
                    variant="standard"
                    placeholder={namePlaceholder}
                    value={automation?.name || ""}
                    onChange={(e) => onChange({ name: e.target.value })}
                    onBlur={() => {
                        if (automation?.name?.trim()) setEditingTitle(false);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && automation?.name?.trim()) {
                            e.preventDefault();
                            setEditingTitle(false);
                        }
                    }}
                    InputProps={{
                        sx: {
                            m: 0,
                            mb: 0,
                            typography: "h5",
                            fontWeight: 500,
                            color: "text.primary",
                            "& .MuiInputBase-input": {
                                py: 0.25,
                                fontWeight: 500,
                                color: "text.primary",
                            },
                            "&:before": {
                                borderBottomColor: "rgba(126, 145, 170, 0.55)",
                            },
                            "&:after": {
                                borderBottomColor: "rgba(126, 145, 170, 0.75)",
                            },
                            "&:hover:not(.Mui-disabled):before": {
                                borderBottomColor: "rgba(126, 145, 170, 0.75)",
                            },
                        },
                    }}
                    sx={{
                        width: { xs: "100%", sm: 420 },
                        maxWidth: "100%",
                        mb: 0,
                        "& .MuiInputBase-root": {
                            mb: 0,
                        },
                    }}
                />
            );
        }

        return (
            <Typography
                variant="h5"
                component="div"
                onClick={() => setEditingTitle(true)}
                sx={{
                    lineHeight: 1.25,
                    cursor: "text",
                    minHeight: 34,
                    display: "flex",
                    alignItems: "center",
                    fontWeight: 500,
                    color: "text.primary",
                    m: 0,
                    mb: 0,
                    pb: 0,
                }}>
                {title}
            </Typography>
        );
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                p: { xs: 1.25, md: 1.5 },
                borderRadius: 3,
                background: "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(243,248,255,0.92) 100%)",
                borderColor: "rgba(103, 58, 183, 0.18)",
            }}>
            <Stack spacing={1.25}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    flexWrap="wrap"
                    useFlexGap>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        <Tooltip title="Volver al listado">
                            <IconButton size="small" onClick={onBack}>
                                <ArrowBackOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Box sx={{ flex: "0 1 auto", minWidth: 0, display: "flex", alignItems: "center" }}>
                            {renderTitle()}
                        </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexShrink: 0 }}>
                        {controls}
                        <FormControlLabel
                            sx={{ mr: 0 }}
                            control={(
                                <Switch
                                    checked={automation?.enabled !== false}
                                    onChange={(e) => onChange({ enabled: e.target.checked })}
                                />
                            )}
                            label={automation?.enabled !== false ? "Activa" : "Pausada"}
                        />
                    </Stack>
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={0.75} alignItems={{ xs: "flex-start", sm: "center" }}>
                    <TextAddButton onClick={() => setShowDescription(true)}>
                        {showDescription ? "Editar descripción" : "Agregar descripción"}
                    </TextAddButton>
                    <TextAddButton onClick={() => setShowGroup(true)}>
                        {showGroup ? "Editar grupo" : "Asignar grupo"}
                    </TextAddButton>
                </Stack>

                {(showDescription || showGroup) && (
                    // En una caja: como hijo directo del Stack, la grilla pierde su margen negativo y queda corrida.
                    <Box>
                        <Grid container spacing={1.5}>
                            {showDescription && (
                                <Grid item xs={12} md={showGroup ? 7 : 12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        label="Descripción (opcional)"
                                        value={automation?.description || ""}
                                        onChange={(e) => onChange({ description: e.target.value })}
                                    />
                                </Grid>
                            )}
                            {showGroup && (
                                <Grid item xs={12} md={showDescription ? 5 : 12}>
                                    <Autocomplete
                                        freeSolo
                                        options={groupOptions}
                                        value={automation?.notification_group || ""}
                                        inputValue={automation?.notification_group || ""}
                                        onInputChange={(_event, value) => onChange({ notification_group: value })}
                                        onChange={(_event, value) => onChange({ notification_group: value || "" })}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Grupo"
                                                placeholder={UNGROUPED_GROUP_LABEL}
                                                helperText={`Déjalo vacío para mostrarla bajo ${UNGROUPED_GROUP_LABEL}.`}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                )}
            </Stack>
        </Paper>
    );
};

export default AutomationMetaHeader;
